import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { FrequencyBadge } from '@/components/shared/FrequencyBadge'
import { useBasePath } from '@/lib/paths'
import { LicenseSummary } from '@/types'
import { Shield, Plus, AlertCircle } from 'lucide-react'


type Props = {
  licenses: LicenseSummary[]
  search: string
  pagination: PaginationMeta
}

export default function LicensesIndex({ licenses, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title="Licenses" breadcrumbs={[{ label: 'Licenses' }]}>
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Licenses</h1>
          <p className="text-sm text-muted-foreground">Manage your business licenses and permits</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={`${basePath}/licenses/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New License
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search licenses..." />
        </div>

        {licenses.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Shield className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No licenses yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Start tracking your first license</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/licenses/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Get Started
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {licenses.map((license, i) => (
              <Link
                key={license.id}
                href={`${basePath}/licenses/${license.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{license.display_name}</span>
                      <StatusBadge status={license.status} />
                      <FrequencyBadge frequency={license.frequency} />
                      {license.perpetual && <Badge variant="outline" className="font-normal text-xs">Perpetual</Badge>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{license.business_name}</span>
                      {license.next_due_date && (
                        <>
                          <span>·</span>
                          <span className={license.next_due_date && new Date(license.next_due_date) < new Date() ? 'text-red-600 font-medium flex items-center gap-1' : ''}>
                            {license.next_due_date && new Date(license.next_due_date) < new Date() && <AlertCircle className="w-3 h-3" />}
                            Due: {new Date(license.next_due_date).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {license.latest_record_stage && <StageBadge stage={license.latest_record_stage} variant="license" />}
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
